import { Button, Table } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type {
    SupplierRequestComingTransferDest,
    SupplierRequestComingType,
    WarehouseInfo,
} from '@econtractor/types';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withPrimaryCurrency } from 'utils/helpers/currency/currencyHelpers';
import { useSettings } from 'context/SettingsContext';
import { CountBadge, PaymentCloudFiles } from 'modules/warehouse';
import {
    COMING_TRANSFER_NONE,
    COMING_TRANSFER_OBJECT,
    COMING_TRANSFER_RESERVE,
} from '../model/constants';
import {
    comingMatchesObjectFilter,
    getObjectStatusFromWarehouses,
    hasReserveWarehouse,
    isComingObjectOpen,
} from '../model/comingObject';
import { safeIntParse } from 'utils/helpers/safeParsing';
import spinnerTitle from 'components/others/spinnerTitle';

interface ComingBatchesProps {
    comings: SupplierRequestComingType[];
    selectedObjectKey: number;
    canOps: boolean;
    disabled?: boolean;
    transferringComingId: number | null;
    onTransfer: (
        coming: SupplierRequestComingType,
        dest: SupplierRequestComingTransferDest,
    ) => void;
}

const ComingBatches = ({
    comings,
    selectedObjectKey,
    canOps,
    disabled = false,
    transferringComingId,
    onTransfer,
}: ComingBatchesProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];

    const numbered = comings.map((coming, index) => ({
        coming,
        batchNo: comings.length - index,
    }));
    const rows = numbered.filter(({ coming }) =>
        comingMatchesObjectFilter(coming.objectId, selectedObjectKey),
    );
    if (!rows.length) return null;

    return (
        <div className="mb-3">
            <h6 className="text-muted mb-2">{t('supplierRequests.view.comingsTitle')}</h6>
            {rows.map(({ coming, batchNo }) => {
                const dt = DT.fromMillis(Number(coming.dt || 0));
                const objectId = safeIntParse(coming.objectId) || 0;
                const transferredTo = Number(coming.transferredTo || COMING_TRANSFER_NONE);
                const canTransfer =
                    canOps &&
                    objectId > 0 &&
                    transferredTo === COMING_TRANSFER_NONE &&
                    isComingObjectOpen(
                        coming.objectStatus ??
                            getObjectStatusFromWarehouses(objectId, warehouses),
                    );
                const showReserve = canTransfer && hasReserveWarehouse(warehouses, objectId);
                const isTransferring = transferringComingId === coming.ID;
                const busy = disabled || transferringComingId != null;

                return (
                    <div key={coming.ID} className="border rounded mb-2 p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                            <span className="fw-medium">
                                {t('supplierRequests.view.comingBatch', { id: batchNo })}
                            </span>
                            <span className="text-muted f-12">
                                {dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '—'}
                            </span>
                        </div>
                        {transferredTo === COMING_TRANSFER_OBJECT ? (
                            <p className="text-success f-12 mb-2">
                                {t('supplierRequests.view.transferredToObject')}
                            </p>
                        ) : null}
                        {transferredTo === COMING_TRANSFER_RESERVE ? (
                            <p className="text-success f-12 mb-2">
                                {t('supplierRequests.view.transferredToReserve')}
                            </p>
                        ) : null}
                        <div className="crm-table-wrap">
                            <Table size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr className="bg-light">
                                        <th>{t('supplierRequests.create.colName')}</th>
                                        <th className="text-end">
                                            {t('comingFields.price')}
                                        </th>
                                        <th className="text-end">
                                            {t('supplierRequests.create.colCount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(coming.items || []).map((item) => (
                                        <tr key={item.ID}>
                                            <td>
                                                <div>{item.prodName}</div>
                                                {item.prodArticle ? (
                                                    <div className="text-muted f-12">
                                                        {item.prodArticle}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="text-end">
                                                {withPrimaryCurrency(formatPrice(item.price), rate)}
                                            </td>
                                            <td className="text-end">
                                                <CountBadge count={item.itemCount} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {coming.paymentAttachCloud?.length ? (
                            <div className="mt-2 px-3">
                                <PaymentCloudFiles
                                    paymentId={Number(coming.paymentId || 0)}
                                    files={coming.paymentAttachCloud}
                                />
                            </div>
                        ) : null}
                        {canTransfer ? (
                            <div className="d-flex flex-wrap justify-content-end gap-2 mt-2">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    disabled={busy}
                                    onClick={() => onTransfer(coming, 'object')}
                                >
                                    {spinnerTitle(
                                        t('supplierRequests.view.btnTransferToObject'),
                                        isTransferring,
                                        t('common:buttons.saving'),
                                    )}
                                </Button>
                                {showReserve ? (
                                    <Button
                                        size="sm"
                                        variant="outline-info"
                                        disabled={busy}
                                        onClick={() => onTransfer(coming, 'reserve')}
                                    >
                                        {spinnerTitle(
                                            t('supplierRequests.view.btnTransferToReserve'),
                                            isTransferring,
                                            t('common:buttons.saving'),
                                        )}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

export default ComingBatches;
